import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import BouncingDots from "../../loading/BouncingDots";
import h from "../../../helpers";
import { toJson } from "../../../helpers/typedHelpers";
import { getCompaniesForUser } from "../../../reducers/userCompanies";

import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";

const userDetailsPropTypes = {
    // Passed In
    userId: PropTypes.number,
};

type UserDetailsProps = PropTypes.InferProps<typeof userDetailsPropTypes>;

const UserDetails: React.FC<UserDetailsProps> = ({ userId }: UserDetailsProps) => {
    const [userRoles, setUserRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);

    const user = useSelector(state => state.users.byId[userId]);
    const adminRoles = useSelector(state => state.session.roles || []);
    const userCompanies = useSelector(state => getCompaniesForUser(state, { userId }));
    const [listOfUserFlowsToDelete, setListOfUserFlowsToDelete] = useState([]);
    const [isListOfUserFlowsToDeleteLoading, setIsListOfUserFlowsToDeleteLoading] = useState("IDLE");

    const loadedUserFlowsToDelete = userId => {
        setIsListOfUserFlowsToDeleteLoading("LOADING");
        let myarrayofflows = [];
        let url = "/flows/getUserflows";
        if (userId != null) {
            url += "/" + userId;
        }

        fetch(url, {
            credentials: "same-origin",
            method: "POST",
        })
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                data.flows.map(item => myarrayofflows.push(item.FlowId));
                setListOfUserFlowsToDelete(myarrayofflows);
                setIsListOfUserFlowsToDeleteLoading("IDLE");
            })
            .catch(error => {
                h.error("Error loading flows.", error);
                setIsListOfUserFlowsToDeleteLoading("ERRORED");
            });
    };

    const deleteUserFlows = userId => {
        setIsListOfUserFlowsToDeleteLoading("QUEUEING");
        let url = "/flows/DeleteUserFlows";
        let paramString = "userIdToDelete=" + userId;
        if (userId != null) {
            url += "?" + paramString;
        }
        fetch(url, {
            credentials: "same-origin",
            method: "POST",
        })
            .then(h.checkStatus)
            .then(toJson)
            .then(data => {
                setIsListOfUserFlowsToDeleteLoading("QUEUED");
                return data;
            })
            .catch(error => {
                h.error("Error deleting flows.", error);
                setIsListOfUserFlowsToDeleteLoading("IDLE");
            });
    };

    useEffect(() => {
        if ((adminRoles.includes("Super Admin") || adminRoles.includes("Experian Admin")) && userId && userId != 0) {
            fetch(`/api/roles/user/${userId}`, { credentials: "same-origin" })
                .then(h.checkStatus)
                .then(toJson)
                .then(data => {
                    setUserRoles(data.roles);
                    setLoadingRoles(false);
                })
                .catch(error => {
                    setLoadingRoles(false);
                    h.error("Error loading User Roles.", error);
                });
            loadedUserFlowsToDelete(userId);
        }
    }, [userId, adminRoles]);

    const renderExpireDate = value => {
        let content = "NA";
        if (value != null) {
            const dateValue = new Date(value);
            content = dateValue.toLocaleDateString();
        }
        return content;
    };

    const renderUserRoles = () => {
        if (!(adminRoles.includes("Super Admin") || adminRoles.includes("Experian Admin"))) {
            return <div></div>;
        }

        if (loadingRoles) {
            return (
                <div style={{ paddingLeft: "42%" }}>
                    <BouncingDots />
                </div>
            );
        }

        let content = "";
        if (userRoles.length <= 0) {
            content = <div style={{ marginLeft: "15px" }}>No roles found for this user.</div>;
        } else {
            content = userRoles.map(r => renderUserRole(r));
        }

        return (
            <List
                aria-label="user-roles-list"
                style={{ padding: "0px", overflow: "auto", maxHeight: "300px" }}
                subheader={
                    <ListSubheader component="div" disableGutters id="roles-list-subheader">
                        <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                            <strong>User Roles</strong>
                        </h4>
                    </ListSubheader>
                }
            >
                {content}
            </List>
        );
    };

    const renderUserRole = userRole => (
        <div key={userRole.Id}>
            <Divider />
            <ListItem>{userRole.Name}</ListItem>
        </div>
    );

    const renderUserCompanies = () => {
        let content = "";
        if (userCompanies.length <= 0) {
            content = <div style={{ marginLeft: "15px" }}>No companies found for this user.</div>;
        } else {
            content = userCompanies.map(c => renderUserCompany(c));
        }

        return (
            <List
                aria-label="user-companies-list"
                style={{ padding: "0px", overflow: "auto", maxHeight: "300px" }}
                subheader={
                    <ListSubheader component="div" disableGutters id="roles-list-subheader">
                        <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                            <strong>User Companies</strong>
                        </h4>
                    </ListSubheader>
                }
            >
                {content}
            </List>
        );
    };

    const renderUserCompany = company => (
        <div key={company.CompanyID}>
            <Divider />
            <ListItem>{company.CompanyName}</ListItem>
        </div>
    );

    const renderUserFlows = () => {
        let content = "";
        if (isListOfUserFlowsToDeleteLoading == "LOADING" || isListOfUserFlowsToDeleteLoading == "QUEUEING") {
            content = <BouncingDots />;
            return (
                <div>
                    <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                        <strong>User Flows</strong>
                    </h4>
                    <div style={{ paddingLeft: "42%" }}>{content}</div>
                </div>
            );
        }
        if (isListOfUserFlowsToDeleteLoading == "ERRORED") {
            content = "Error Loading Flows";
            return (
                <div>
                    <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                        <strong>User Flows</strong>
                    </h4>
                    <label style={{ color: "red" }}>{content}</label>
                </div>
            );
        }
        if (isListOfUserFlowsToDeleteLoading == "QUEUED") {
            content = "User flows have been queued for deletion.";
            return (
                <div>
                    <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                        <strong>User Flows</strong>
                    </h4>
                    <label style={{ color: "black" }}>{content}</label>
                </div>
            );
        }
        if (listOfUserFlowsToDelete.length <= 0) {
            content = <div style={{ marginLeft: "15px" }}>No flows found for this user.</div>;
            return (
                <div>
                    <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                        <strong>User Flows</strong>
                    </h4>
                    <label style={{ color: "black" }}>{content}</label>
                </div>
            );
        } else {
            content =
                "WARNING: User has Active Flows! " + listOfUserFlowsToDelete.length + " Flows Found for this user";
            return (
                <div>
                    <h4 style={{ margin: "0px 0px 5px 0px", paddingLeft: "5px", color: "#000" }}>
                        <strong>User Flows</strong>
                    </h4>
                    <label style={{ color: "red" }}>{content}</label>
                    <button
                        className="material-button transparent"
                        style={{
                            border: "2px solid #fff",
                            margin: "0px 5px",
                            height: "35px",
                            padding: "4px 20px",
                            backgroundColor: "#0069a9",
                            fontSize: "16px",
                        }}
                        onClick={() => {
                            deleteUserFlows(userId);
                        }}
                    >
                        Click Here to Delete User Flows
                    </button>
                </div>
            );
        }
    };

    return user ? (
        <div>
            <div style={{ marginBottom: "20px" }}>
                <h4 style={{ paddingLeft: "5px", margin: "5px 0px", color: "#000" }}>
                    <strong>User Information</strong>
                </h4>
                <Divider style={{ marginBottom: "5px" }} />
                <div style={{ paddingLeft: "16px" }}>
                    <div>
                        <strong>User Name:</strong> {user.UserName}
                    </div>
                    <div style={{ display: "inline-block" }}>
                        <strong>User Active:</strong>{" "}
                        {user.UserActive ? (
                            <i
                                className="material-icons"
                                style={{
                                    display: "inline-block",
                                    verticalAlign: "middle",
                                    height: "20px",
                                    width: "20px",
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                    color: "#406eb3",
                                }}
                            >
                                check
                            </i>
                        ) : (
                            <i
                                className="material-icons"
                                style={{
                                    display: "inline-block",
                                    verticalAlign: "middle",
                                    height: "20px",
                                    width: "20px",
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                    color: "#e25252",
                                }}
                            >
                                close
                            </i>
                        )}
                    </div>
                    <div>
                        <strong>Expire Date:</strong> {renderExpireDate(user.ExpireDate)}
                    </div>
                </div>
            </div>
            <div style={{ marginBottom: "20px" }}>{renderUserRoles()}</div>
            <div style={{ marginBottom: "20px" }}>{renderUserFlows()}</div>
            <div style={{ marginBottom: "20px" }}>{renderUserCompanies()}</div>
        </div>
    ) : null;
};

export default UserDetails;

/*
Saving for possible use with typescript changes

import type { Company, User, Role } from "../../../types/types";

type Props = {
    //passed in
    userId: number,
    //redux
    user: User,
    userCompanies: Array<Company>,
};

type State = {
    userRoles: Array<Role>,
    loadingRoles: boolean,
};
*/
